<template>
  <div class="executive-analytics-view">
    <!-- Header Banner -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card bg-gradient-dark border-0 shadow-lg text-white" style="background: linear-gradient(135deg, #161a29 0%, #0d101a 100%); border-left: 4px solid #ff9900 !important; border-radius: 12px;">
          <div class="card-body p-3 p-md-4">
            <div class="row align-items-center">
              <div class="col-lg-8">
                <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 8px;">
                  <span class="badge badge-warning text-dark font-weight-bold">
                    <i class="mdi mdi-crown mr-1"></i> {{ $t('executive.suiteBadge') }}
                  </span>
                  <span class="badge badge-outline-info">
                    <i class="mdi mdi-radar mr-1"></i> {{ $t('executive.macroRadar') }}
                  </span>
                  <span class="badge badge-outline-success">
                    <i class="mdi mdi-robot mr-1"></i> {{ $t('executive.geminiAi') }}
                  </span>
                </div>
                <h2 class="font-weight-bold mb-2 text-white">
                  {{ $t('executive.title') }}
                </h2>
                <p class="text-muted mb-0" style="font-size: 14px; line-height: 1.6;">
                  {{ $t('executive.description') }}
                </p>
              </div>
              <div class="col-lg-4 text-lg-right mt-3 mt-lg-0">
                <div class="d-flex flex-column flex-sm-row justify-content-lg-end" style="gap: 8px;">
                  <button class="btn btn-outline-warning font-weight-bold" @click="downloadAuditReport">
                    <i class="mdi mdi-file-pdf-box mr-1"></i> {{ $t('executive.exportPdf') }}
                  </button>
                  <button class="btn btn-warning text-dark font-weight-bold" @click="fetchMacroAnalytics">
                    <i class="mdi mdi-refresh mr-1"></i> {{ $t('executive.syncTelemetry') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Macro Economics & Operational KPI Cards -->
    <div class="row mb-4">
      <!-- Total Gold Circulating & Earned/Redeemed Ledger -->
      <div class="col-xl-3 col-sm-6 grid-margin stretch-card">
        <div class="card bg-dark border-secondary">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-muted small font-weight-bold">{{ $t('executive.totalGoldLiquidity') }}</span>
              <span class="badge badge-warning text-dark font-weight-bold">{{ $t('executive.goldRate') }}</span>
            </div>
            <div class="d-flex align-items-baseline mb-1">
              <h3 class="mb-0 text-white font-weight-bold mr-2">{{ formatNumber(macroData.macro_economics.total_gold_circulating) }}</h3>
              <span class="text-warning small font-weight-bold">{{ $t('executive.goldCirculating') }}</span>
            </div>
            <p class="text-muted mb-2 small">
              {{ $t('executive.totalGoldEarnedLabel') }}: <strong class="text-white">{{ formatNumber(macroData.macro_economics.total_gold_earned) }} G</strong>
            </p>
            <div class="progress progress-sm bg-secondary" style="height: 6px;">
              <div class="progress-bar bg-warning" role="progressbar" :style="{ width: Math.min(100, macroData.macro_economics.redemption_rate_percent || 0) + '%' }"></div>
            </div>
            <div class="d-flex justify-content-between text-muted small mt-1">
              <span>{{ $t('executive.burnRate') }}: {{ macroData.macro_economics.redemption_rate_percent ?? 0 }}%</span>
              <span>{{ $t('executive.totalGoldRedeemedLabel') }}: {{ formatNumber(macroData.macro_economics.total_gold_redeemed) }} G</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Verified Quest Approval & Completion Volume -->
      <div class="col-xl-3 col-sm-6 grid-margin stretch-card">
        <div class="card bg-dark border-secondary">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-muted small font-weight-bold">{{ $t('executive.netCorporateRoi') }}</span>
              <span class="badge badge-success font-weight-bold">{{ macroData.macro_economics.approval_rate_percent ?? 0 }}%</span>
            </div>
            <div class="d-flex align-items-baseline mb-1">
              <h3 class="mb-0 text-success font-weight-bold mr-2">{{ formatNumber(macroData.macro_economics.total_approved_quests) }}</h3>
              <span class="text-muted small">{{ $t('executive.returnOnIncentive') }}</span>
            </div>
            <p class="text-muted mb-2 small">
              {{ $t('executive.roiDesc') }}
            </p>
            <div class="d-flex align-items-center text-success small">
              <i class="mdi mdi-check-decagram mr-1" aria-hidden="true"></i>
              <span>{{ formatNumber(macroData.macro_economics.total_submissions) }} {{ $t('executive.roiBenchmark') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Company Velocity & Health Index -->
      <div class="col-xl-3 col-sm-6 grid-margin stretch-card">
        <div class="card bg-dark border-secondary">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-muted small font-weight-bold">{{ $t('executive.velocityIndex') }}</span>
              <span class="badge badge-info font-weight-bold">{{ $t('executive.highSpeed') }}</span>
            </div>
            <div class="d-flex align-items-baseline mb-1">
              <h3 class="mb-0 text-info font-weight-bold mr-2">{{ averageVelocityScore }}</h3>
              <span class="text-muted small">/ 100</span>
            </div>
            <p class="text-muted mb-2 small">
              {{ $t('executive.velocitySub') }}
            </p>
            <div class="d-flex align-items-center text-info small">
              <i class="mdi mdi-lightning-bolt mr-1" aria-hidden="true"></i>
              <span>{{ averageCompletionRate }}% {{ $t('executive.activeDailyEmployees') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Deployed Realms -->
      <div class="col-xl-3 col-sm-6 grid-margin stretch-card">
        <div class="card bg-dark border-secondary">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-muted small font-weight-bold">{{ $t('executive.activeRealms') }}</span>
              <router-link to="/realms/marketplace" class="badge badge-outline-warning">{{ $t('executive.manageMarketplace') }}</router-link>
            </div>
            <div class="d-flex align-items-baseline mb-1">
              <h3 class="mb-0 text-white font-weight-bold mr-2">{{ macroData.macro_economics.active_realms_count ?? 0 }}</h3>
              <span class="text-muted small">{{ $t('executive.realmsDeployed') }}</span>
            </div>
            <p class="text-muted mb-2 small">
              {{ activeDivisionNames }}
            </p>
            <div class="d-flex align-items-center text-warning small">
              <i class="mdi mdi-shield-check mr-1" aria-hidden="true"></i>
              <span>{{ $t('executive.rulesVerified') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Company Boss Raid Macro Orchestrator -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card border-0 shadow-lg text-white" style="background: linear-gradient(135deg, #2b1118 0%, #151828 100%); border-radius: 12px; border: 1px solid rgba(239, 68, 68, 0.3);">
          <div class="card-body p-4">
            <div class="row align-items-center">
              <div class="col-lg-7">
                <div class="d-flex align-items-center mb-2" style="gap: 8px;">
                  <span class="badge badge-danger font-weight-bold px-2 py-1">
                    <i class="mdi mdi-sword-cross mr-1" aria-hidden="true"></i> {{ $t('executive.bossRaidTitle') }}
                  </span>
                  <span v-if="Number(macroData.company_boss_raid.target_nominal) > 0" class="badge badge-outline-light">
                    {{ $t('executive.quarterTarget') }}: {{ Number(macroData.company_boss_raid.target_nominal) >= 1000000 ? ('Rp ' + formatNumber(macroData.company_boss_raid.target_nominal)) : (formatNumber(macroData.company_boss_raid.target_nominal) + ' HP') }}
                  </span>
                </div>
                <h3 class="font-weight-bold text-white mb-2">
                  {{ macroData.company_boss_raid.boss_name || $t('executive.noActiveBossRaid') }}
                </h3>
                <p class="text-muted mb-3" style="font-size: 14px;">
                  {{ $t('executive.bossRaidDesc') }}
                </p>

                <!-- HP Progress Bar -->
                <div class="mb-2">
                  <div class="d-flex justify-content-between small font-weight-bold mb-1">
                    <span class="text-danger">{{ $t('executive.hpRemaining') }}: {{ macroData.company_boss_raid.hp_remaining_percent ?? 0 }}%</span>
                    <span class="text-success">{{ $t('executive.damageDealt') }}: {{ Number(macroData.company_boss_raid.target_nominal) >= 1000000 ? ('Rp ' + formatNumber(macroData.company_boss_raid.current_damage)) : (formatNumber(macroData.company_boss_raid.current_damage) + ' DMG') }}</span>
                  </div>
                  <div class="progress" style="height: 14px; background: rgba(0, 0, 0, 0.5); border-radius: 7px; overflow: hidden;">
                    <div class="progress-bar bg-gradient-danger progress-bar-striped progress-bar-animated" role="progressbar" :style="{ width: (macroData.company_boss_raid.hp_remaining_percent ?? 0) + '%' }"></div>
                  </div>
                </div>
                <div class="d-flex flex-wrap text-muted small" style="gap: 16px;">
                  <span><i class="mdi mdi-account-group text-warning mr-1" aria-hidden="true"></i> {{ $t('executive.contributors') }}: <strong>{{ macroData.company_boss_raid.contributors_count ?? 0 }} {{ $t('executive.staff') }}</strong></span>
                  <span><i class="mdi mdi-clock-outline text-info mr-1" aria-hidden="true"></i> {{ $t('executive.deadline') }}: <strong>{{ macroData.company_boss_raid.deadline || '-' }}</strong></span>
                  <span><i class="mdi mdi-cash-multiple text-warning mr-1" aria-hidden="true"></i> {{ $t('executive.lootPool') }}: <strong>{{ formatNumber(macroData.company_boss_raid.shared_loot_pool) }} Gold</strong></span>
                </div>
              </div>

              <div class="col-lg-5 text-lg-right mt-3 mt-lg-0">
                <div class="p-3 rounded" style="background: #081121; border: 1px solid #1e3358;">
                  <div class="text-center mb-3">
                    <div class="d-inline-flex align-items-center justify-content-center mb-2" style="width: 44px; height: 44px; border-radius: 10px; background: rgba(251, 191, 36, 0.12); border: 1px solid rgba(251, 191, 36, 0.35);">
                      <i class="mdi mdi-shield-sword-outline" style="font-size: 22px; color: #fbbf24;" aria-hidden="true"></i>
                    </div>
                    <div class="font-weight-bold text-white">{{ macroData.company_boss_raid.boss_name || $t('executive.noActiveBossRaid') }}</div>
                    <small class="text-muted">{{ $t('executive.projectedDefeat') }}</small>
                  </div>
                  <div class="d-flex justify-content-center" style="gap: 8px;">
                    <button class="btn btn-warning font-weight-bold btn-sm" @click="simulateRaidBoost">
                      <i class="mdi mdi-flash mr-1" aria-hidden="true"></i> {{ $t('executive.triggerBoost') }}
                    </button>
                    <button class="btn btn-outline-light btn-sm" @click="openAdjustBossModal">
                      <i class="mdi mdi-cog mr-1" aria-hidden="true"></i> {{ $t('executive.adjustTarget') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Division Squad Heatmap (Full Width) -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card bg-dark border-secondary">
          <div class="card-body p-3 p-md-4">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap: 8px;">
              <div>
                <h4 class="card-title text-white mb-1">
                  <i class="mdi mdi-fire text-warning mr-1" aria-hidden="true"></i> {{ $t('executive.divisionHeatmap') }}
                </h4>
                <p class="text-muted small mb-0">{{ $t('executive.divisionHeatmapSub') }}</p>
              </div>
              <div class="btn-group btn-group-sm">
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="heatmapPeriod === '7d' ? 'btn-warning text-dark' : 'btn-outline-secondary text-white'"
                  @click="setHeatmapPeriod('7d')"
                >
                  {{ $t('executive.last7Days') }}
                </button>
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="heatmapPeriod === '30d' ? 'btn-warning text-dark' : 'btn-outline-secondary text-white'"
                  @click="setHeatmapPeriod('30d')"
                >
                  {{ $t('executive.last30Days') }}
                </button>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-sm table-dark table-hover mb-0 align-middle" style="table-layout: fixed; width: 100%;">
                <thead>
                  <tr class="text-muted small text-uppercase">
                    <th style="width: 22%;">{{ $t('executive.divisionSquad') }}</th>
                    <th style="width: 11%;" class="text-center">{{ $t('executive.members') }}</th>
                    <th style="width: 13%;" class="text-center">{{ $t('executive.questsCleared') }}</th>
                    <th style="width: 14%;" class="text-center">{{ $t('executive.completionRate') }}</th>
                    <th style="width: 16%;" class="text-center">{{ $t('executive.velocityScore') }}</th>
                    <th style="width: 13%;" class="text-right">{{ $t('executive.goldCirculating') }}</th>
                    <th style="width: 11%;" class="text-center">{{ $t('executive.status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="div in displayedHeatmap" :key="div.id">
                    <td class="font-weight-bold text-white">
                      <div class="text-truncate" :title="div.name">
                        <i class="mdi mdi-shield-account mr-1 text-info" aria-hidden="true"></i> {{ div.name }}
                      </div>
                    </td>
                    <td class="text-center">{{ div.members }} {{ $t('executive.staff') }}</td>
                    <td class="text-center font-weight-bold text-white">{{ div.quests_cleared_display }}</td>
                    <td class="text-center">
                      <span :class="div.completion_rate_display >= 80 ? 'text-success' : 'text-warning'" class="font-weight-bold">
                        {{ div.completion_rate_display }}%
                      </span>
                    </td>
                    <td class="text-center">
                      <div class="d-flex align-items-center justify-content-center" style="gap: 6px;">
                        <span class="small font-weight-bold text-white">{{ div.velocity_score_display }}</span>
                        <div class="progress" style="width: 60px; height: 6px;">
                          <div class="progress-bar bg-info" :style="{ width: div.velocity_score_display + '%' }"></div>
                        </div>
                      </div>
                    </td>
                    <td class="text-right font-weight-bold text-warning">
                      {{ formatNumber(div.total_gold_display) }} G
                    </td>
                    <td class="text-center">
                      <span class="badge" :class="div.completion_rate_display >= 80 ? 'badge-success' : (div.completion_rate_display >= 60 ? 'badge-warning text-dark' : 'badge-danger')">
                        {{ div.health_status }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="displayedHeatmap.length === 0">
                    <td colspan="7" class="text-center py-4 text-muted">
                      {{ $t('common.noData') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Turnover & Demotivation Risk Radar -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card bg-dark border-secondary">
          <div class="card-body p-3 p-md-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h4 class="card-title text-white mb-1">
                  <i class="mdi mdi-alert-circle text-danger mr-1" aria-hidden="true"></i> {{ $t('executive.turnoverRadar') }}
                </h4>
                <p class="text-muted small mb-0">{{ $t('executive.turnoverRadarSub') }}</p>
              </div>
              <span class="badge badge-outline-danger">{{ macroData.turnover_radar.length }} {{ $t('executive.detectedCount') }}</span>
            </div>

            <div v-if="macroData.turnover_radar.length === 0" class="text-center py-4 text-muted">
              <i class="mdi mdi-emoticon-happy-outline text-success" style="font-size: 32px;" aria-hidden="true"></i>
              <p class="mb-0 mt-2">{{ $t('executive.allConsistent') }}</p>
            </div>

            <div v-else class="list-group list-group-flush bg-transparent">
              <div
                v-for="player in macroData.turnover_radar"
                :key="player.id"
                class="list-group-item bg-transparent border-secondary px-0 py-2 d-flex justify-content-between align-items-center"
              >
                <div>
                  <div class="font-weight-bold text-white d-flex align-items-center" style="gap: 6px;">
                    <span>{{ player.name }}</span>
                    <span class="badge badge-dark text-muted font-weight-normal">Lv.{{ player.level }}</span>
                    <span v-if="player.rescue_sent" class="badge badge-success font-weight-bold">{{ $t('executive.rescueDispatched') }}</span>
                  </div>
                  <small class="text-danger">
                    <i class="mdi mdi-fire-off mr-1" aria-hidden="true"></i>{{ $t('executive.streakLost') }} {{ player.streak_lost }} {{ $t('executive.days') }} • {{ $t('executive.inactiveDays') }} {{ player.days_inactive }} {{ $t('executive.days') }}
                  </small>
                </div>
                <div>
                  <button
                    class="btn btn-sm"
                    :class="player.rescue_sent ? 'btn-success' : 'btn-outline-warning'"
                    :disabled="player.rescue_sent"
                    @click="triggerMakerRescue(player)"
                  >
                    <i class="mdi" :class="player.rescue_sent ? 'mdi-check-circle mr-1' : 'mdi-hand-heart mr-1'" aria-hidden="true"></i>
                    {{ player.rescue_sent ? $t('executive.rescueDispatched') : $t('executive.callMaker') }}
                  </button>
                </div>
              </div>
            </div>

            <div class="mt-3 p-2 rounded" style="background: rgba(239, 68, 68, 0.1); border: 1px dashed rgba(239, 68, 68, 0.3);">
              <small class="text-muted d-block">
                <i class="mdi mdi-information-outline text-danger mr-1" aria-hidden="true"></i> <strong>{{ $t('executive.proactiveAlgo') }}</strong> {{ $t('executive.proactiveDesc') }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Financial & Tax Ledger (Real Paid Billing Transactions & PPN 11% Audit) -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card bg-dark border-secondary">
          <div class="card-body p-3 p-md-4">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap: 8px;">
              <div>
                <h4 class="card-title text-white mb-1">
                  <i class="mdi mdi-cash-register text-success mr-1" aria-hidden="true"></i> {{ $t('executive.financialLedger') }}
                </h4>
                <p class="text-muted small mb-0">{{ $t('executive.financialLedgerSub') }}</p>
              </div>
              <div class="d-flex align-items-center" style="gap: 8px;">
                <span class="badge badge-success">{{ $t('executive.autoDisburseActive') }}</span>
                <span class="badge badge-outline-light">{{ $t('executive.autoTaxInvoice') }}</span>
              </div>
            </div>

            <div class="row text-center mb-3">
              <div class="col-md-3 col-6 border-right border-secondary mb-2 mb-md-0">
                <span class="text-muted small">{{ $t('executive.grossRewardQ4') }}</span>
                <h4 class="text-white font-weight-bold mb-0">Rp {{ formatNumber(ledgerSummary.grossBilling) }}</h4>
              </div>
              <div class="col-md-3 col-6 border-right border-secondary mb-2 mb-md-0">
                <span class="text-muted small">{{ $t('executive.ppnCollected') }}</span>
                <h4 class="text-warning font-weight-bold mb-0">Rp {{ formatNumber(ledgerSummary.ppnCollected) }}</h4>
              </div>
              <div class="col-md-3 col-6 border-right border-secondary mb-2 mb-md-0">
                <span class="text-muted small">{{ $t('executive.platformFee') }}</span>
                <h4 class="text-info font-weight-bold mb-0">Rp {{ formatNumber(ledgerSummary.discountApplied) }}</h4>
              </div>
              <div class="col-md-3 col-6">
                <span class="text-muted small">{{ $t('executive.netDisbursed') }}</span>
                <h4 class="text-success font-weight-bold mb-0">Rp {{ formatNumber(ledgerSummary.netPaid) }}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Gemini Strategic Executive Insights -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card bg-dark border-secondary">
          <div class="card-body p-3 p-md-4">
            <div class="d-flex align-items-center mb-3">
              <div class="p-2 rounded mr-2" style="background: linear-gradient(135deg, #6366f1, #3b82f6);">
                <i class="mdi mdi-lightbulb-on text-white" style="font-size: 20px;" aria-hidden="true"></i>
              </div>
              <div>
                <h4 class="card-title text-white mb-0">{{ $t('executive.geminiAdvisory') }}</h4>
                <p class="text-muted small mb-0">{{ $t('executive.geminiAdvisorySub') }}</p>
              </div>
            </div>

            <div v-if="macroData.ai_insights && macroData.ai_insights.length > 0" class="row">
              <div v-for="(insight, idx) in macroData.ai_insights" :key="idx" class="col-md-4 mb-3 mb-md-0">
                <div class="p-3 rounded h-100" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08);">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="font-weight-bold text-info small text-uppercase">{{ insight.category }}</span>
                    <span class="badge badge-outline-warning" style="font-size: 10px;">{{ insight.badge }}</span>
                  </div>
                  <p class="text-white small mb-0" style="line-height: 1.6;">
                    {{ insight.insight }}
                  </p>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-3 text-muted small">
              {{ $t('common.noData') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config/config';
import store from '@/store';
import Swal from 'sweetalert2';

export default {
  name: 'ExecutiveAnalyticsView',
  data() {
    return {
      heatmapPeriod: '7d',
      loading: false,
      raidBoostActive: false,
      bossEditForm: {
        boss_name: '',
        target_nominal: 0,
        shared_loot_pool: 0,
      },
      macroData: {
        macro_economics: {
          total_gold_circulating: 0,
          total_gold_earned: 0,
          total_gold_redeemed: 0,
          redemption_rate_percent: 0,
          total_submissions: 0,
          total_approved_quests: 0,
          approved_quests_period: 0,
          approval_rate_percent: 0,
          active_realms_count: 0,
          paid_billing_gross_idr: 0,
          paid_billing_ppn_idr: 0,
          paid_billing_discount_idr: 0,
          paid_billing_net_idr: 0,
        },
        divisions_heatmap: [],
        turnover_radar: [],
        company_boss_raid: {
          boss_name: '',
          target_nominal: 0,
          current_damage: 0,
          hp_remaining_percent: 0,
          shared_loot_pool: 0,
          deadline: '-',
          contributors_count: 0,
          status: 'INACTIVE',
        },
        ai_insights: [],
      },
    };
  },
  watch: {
    '$currentLocale'() {
      this.fetchMacroAnalytics(this.heatmapPeriod);
    },
  },
  computed: {
    averageVelocityScore() {
      const list = this.macroData?.divisions_heatmap || [];
      if (!list.length) return '0.0';
      const sum = list.reduce((acc, d) => acc + (Number(d.velocity_score) || 0), 0);
      return (sum / list.length).toFixed(1);
    },
    averageCompletionRate() {
      const list = this.macroData?.divisions_heatmap || [];
      if (!list.length) return '0.0';
      const sum = list.reduce((acc, d) => acc + (Number(d.completion_rate) || 0), 0);
      return (sum / list.length).toFixed(1);
    },
    activeDivisionNames() {
      const list = (this.macroData?.divisions_heatmap || []).map((d) => d.name).filter(Boolean);
      return list.length ? list.slice(0, 3).join(', ') : '-';
    },
    ledgerSummary() {
      const econ = this.macroData?.macro_economics || {};
      return {
        grossBilling: Number(econ.paid_billing_gross_idr ?? 0),
        ppnCollected: Number(econ.paid_billing_ppn_idr ?? 0),
        discountApplied: Number(econ.paid_billing_discount_idr ?? 0),
        netPaid: Number(econ.paid_billing_net_idr ?? 0),
      };
    },
    displayedHeatmap() {
      const is30d = this.heatmapPeriod === '30d';
      return (this.macroData.divisions_heatmap || []).map((div) => {
        const quests = Number(
          div.quests_cleared ??
          (is30d ? div.quests_cleared_30d : div.quests_cleared_7d) ??
          div.quests_cleared_7d ??
          0
        );
        const baseGold = Number(div.total_gold) || 0;
        const baseComp = Number(div.completion_rate) || 0;
        const baseVel = Number(div.velocity_score) || 0;
        return {
          ...div,
          quests_cleared_display: quests,
          completion_rate_display: baseComp,
          velocity_score_display: baseVel,
          total_gold_display: baseGold,
        };
      });
    },
  },
  mounted() {
    this.fetchMacroAnalytics();
  },
  methods: {
    getHeaders() {
      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      return { Authorization: `Bearer ${token}` };
    },
    setHeatmapPeriod(days) {
      const normalized = String(days).endsWith('d') ? String(days) : `${days}d`;
      if (this.heatmapPeriod === normalized) return;
      this.heatmapPeriod = normalized;
      this.fetchMacroAnalytics(normalized);
    },
    formatNumber(val) {
      if (!val) return '0';
      return Math.round(Number(val) || 0).toLocaleString(this.$currentLocale === 'id' ? 'id-ID' : 'en-US');
    },
    sanitizeCsvCell(val) {
      const str = String(val ?? '').replace(/"/g, '""');
      if (/^[=+\-@\t\r]/.test(str)) {
        return `"'${str}"`;
      }
      return `"${str}"`;
    },
    async fetchMacroAnalytics(period = this.heatmapPeriod) {
      this.loading = true;
      try {
        const normalizedPeriod = String(period).endsWith('d') ? String(period) : `${period}d`;
        const res = await axios.get(`${config.API_URL}v1/owner/dashboard/macro-analytics?period=${normalizedPeriod}`, {
          headers: this.getHeaders(),
        });
        if (res.data && res.data.data) {
          this.macroData = res.data.data;
          if (this.macroData.company_boss_raid) {
            this.bossEditForm = {
              boss_name: this.macroData.company_boss_raid.boss_name || '',
              target_nominal: Number(this.macroData.company_boss_raid.target_nominal) || 0,
              shared_loot_pool: Number(this.macroData.company_boss_raid.shared_loot_pool) || 0,
            };
          }
        }
      } catch (err) {
        console.warn('Failed to fetch macro analytics:', err);
      } finally {
        this.loading = false;
      }
    },
    async triggerMakerRescue(person) {
      try {
        await axios.post(
          `${config.API_URL}v1/owner/dashboard/rescue-nudge`,
          { player_id: person.id },
          { headers: this.getHeaders() }
        );
      } catch (err) {
        console.warn('Rescue nudge API error:', err);
      }
      this.$set(person, 'rescue_sent', true);
      Swal.fire({
        icon: 'info',
        title: this.$t('executive.rescueSignalTitle'),
        text: this.$t('executive.rescueSignalMsg', { name: person.name }),
        confirmButtonColor: '#ffab00',
        background: '#12141c',
        color: '#fff',
      });
    },
    simulateRaidBoost() {
      this.raidBoostActive = true;
      const raid = this.macroData.company_boss_raid;
      if (raid && raid.target_nominal > 0) {
        const boostDamage = Math.round(raid.target_nominal * 0.05);
        raid.current_damage = Math.min(raid.target_nominal, (raid.current_damage || 0) + boostDamage);
        raid.hp_remaining_percent = Math.max(0, 100 - Math.min(100, Math.round((raid.current_damage / raid.target_nominal) * 100)));
        raid.status = this.$t('executive.raidBoostedStatus');
      }
      Swal.fire({
        icon: 'success',
        title: this.$t('executive.raidBoostTitle'),
        text: this.$t('executive.raidBoostMsg'),
        confirmButtonColor: '#10b981',
        background: '#12141c',
        color: '#fff',
      });
    },
    async saveBossAdjustment() {
      const payload = {
        boss_name: this.bossEditForm.boss_name || this.macroData.company_boss_raid.boss_name || this.$t('executive.defaultBossName'),
        target_nominal: Number(this.bossEditForm.target_nominal || this.macroData.company_boss_raid.target_nominal || 1000),
        shared_loot_pool: Number(this.bossEditForm.shared_loot_pool || this.macroData.company_boss_raid.shared_loot_pool || 0),
      };
      try {
        const res = await axios.post(
          `${config.API_URL}v1/owner/dashboard/boss-raid/adjust`,
          payload,
          { headers: this.getHeaders() }
        );
        if (res.data && res.data.data) {
          this.macroData.company_boss_raid = {
            ...this.macroData.company_boss_raid,
            ...res.data.data,
          };
          return;
        }
      } catch (err) {
        console.warn('Boss raid adjust API error:', err);
      }
      this.macroData.company_boss_raid.boss_name = payload.boss_name;
      this.macroData.company_boss_raid.target_nominal = payload.target_nominal;
      this.macroData.company_boss_raid.shared_loot_pool = payload.shared_loot_pool;
      if (payload.target_nominal > 0) {
        const dmgPct = Math.min(100, Math.round(((this.macroData.company_boss_raid.current_damage || 0) / payload.target_nominal) * 100));
        this.macroData.company_boss_raid.hp_remaining_percent = Math.max(0, 100 - dmgPct);
      }
    },
    async openAdjustBossModal() {
      const { value: newTarget } = await Swal.fire({
        title: this.$t('executive.adjustBossModalTitle'),
        input: 'number',
        inputLabel: this.$t('executive.adjustBossModalLabel'),
        inputValue: this.macroData.company_boss_raid.target_nominal || 1000,
        showCancelButton: true,
        confirmButtonText: this.$t('common.save'),
        cancelButtonText: this.$t('common.cancel'),
        confirmButtonColor: '#ffab00',
        cancelButtonColor: '#374151',
        background: '#12141c',
        color: '#fff',
      });
      if (newTarget) {
        const parsedTarget = parseInt(newTarget, 10);
        if (parsedTarget > 0) {
          this.bossEditForm = {
            boss_name: this.macroData.company_boss_raid.boss_name || this.$t('executive.defaultBossName'),
            target_nominal: parsedTarget,
            shared_loot_pool: this.macroData.company_boss_raid.shared_loot_pool || 0,
          };
          await this.saveBossAdjustment();
        }
        Swal.fire({
          icon: 'success',
          title: this.$t('common.success'),
          text: this.$t('executive.adjustBossSuccessMsg'),
          timer: 1500,
          showConfirmButton: false,
          background: '#12141c',
          color: '#fff',
        });
      }
    },
    downloadAuditReport() {
      const headers = [
        this.$t('executive.divisionSquad'),
        this.$t('executive.members'),
        `${this.$t('executive.questsCleared')} (${this.heatmapPeriod})`,
        `${this.$t('executive.completionRate')} (%)`,
        this.$t('executive.velocityScore'),
        this.$t('executive.goldCirculating'),
        this.$t('executive.status'),
      ];
      const rows = this.displayedHeatmap.map((d) => [
        this.sanitizeCsvCell(d.name),
        d.members,
        d.quests_cleared_display,
        d.completion_rate_display,
        d.velocity_score_display,
        d.total_gold_display,
        this.sanitizeCsvCell(d.health_status),
      ]);
      const csvContent = [headers.map((h) => this.sanitizeCsvCell(h)).join(','), ...rows.map((r) => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `gamiteam_executive_audit_${this.heatmapPeriod}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: this.$t('executive.auditReportGeneratedTitle'),
        text: this.$t('executive.auditReportGeneratedMsg'),
        timer: 2000,
        showConfirmButton: false,
        background: '#12141c',
        color: '#fff',
      });
    },
  },
};
</script>

<style scoped>
.executive-analytics-view .card {
  border-radius: 10px;
}
.executive-analytics-view .table-dark {
  background-color: transparent;
}
.executive-analytics-view .table-dark td,
.executive-analytics-view .table-dark th {
  border-color: rgba(255, 255, 255, 0.08);
  vertical-align: middle;
}
</style>
